import React, { useEffect, useMemo, useState } from "react";
import { Button, Col, Form, Modal, Row, Spinner } from "react-bootstrap";

export const STAGE_OPTIONS = [
  "NEW",
  "ACCEPTED",
  "ON_THE_WAY",
  "ARRIVED",
  "IN_PROGRESS",
];

export const CANCELLER_OPTIONS = ["REQUESTER", "HELPER"];

export const CHARGE_BASE_OPTIONS = ["CHARGEABLE_TOTAL", "TOKEN_AMOUNT_ONLY"];

const DEFAULT_FORM = {
  stage: "NEW",
  canceler: "REQUESTER",
  active: true,
  effectiveFrom: new Date().toISOString().slice(0, 16),
  effectiveTo: "",
  chargePercent: 0,
  chargeFixedAmount: 0,
  chargeBase: "CHARGEABLE_TOTAL",
  helperPayoutPercentOfCharge: 0,
  helperPayoutFixedAmount: 0,
  oliveebFeePercentOfCharge: 0,
  description: "",
  allowCancel: true,
  requireReason: false,
  allowAutomaticRefund: false,
};

const formatDateInput = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
};

const toPayload = (form) => ({
  stage: form.stage,
  canceler: form.canceler,
  active: Boolean(form.active),
  effectiveFrom: form.effectiveFrom ? new Date(form.effectiveFrom).toISOString() : null,
  effectiveTo: form.effectiveTo ? new Date(form.effectiveTo).toISOString() : null,
  chargePercent: Number(form.chargePercent) || 0,
  chargeFixedAmount: Number(form.chargeFixedAmount) || 0,
  chargeBase: form.chargeBase,
  helperPayoutPercentOfCharge: Number(form.helperPayoutPercentOfCharge) || 0,
  helperPayoutFixedAmount: Number(form.helperPayoutFixedAmount) || 0,
  oliveebFeePercentOfCharge: Number(form.oliveebFeePercentOfCharge) || 0,
  description: form.description || "",
  allowCancel: Boolean(form.allowCancel),
  requireReason: Boolean(form.requireReason),
  allowAutomaticRefund: Boolean(form.allowAutomaticRefund),
});

const fromRuleToForm = (rule) => ({
  stage: rule?.stage || DEFAULT_FORM.stage,
  canceler: rule?.canceler || DEFAULT_FORM.canceler,
  active: Boolean(rule?.active),
  effectiveFrom: formatDateInput(rule?.effectiveFrom) || DEFAULT_FORM.effectiveFrom,
  effectiveTo: formatDateInput(rule?.effectiveTo),
  chargePercent: rule?.chargePercent ?? 0,
  chargeFixedAmount: rule?.chargeFixedAmount ?? 0,
  chargeBase: rule?.chargeBase || DEFAULT_FORM.chargeBase,
  helperPayoutPercentOfCharge: rule?.helperPayoutPercentOfCharge ?? 0,
  helperPayoutFixedAmount: rule?.helperPayoutFixedAmount ?? 0,
  oliveebFeePercentOfCharge: rule?.oliveebFeePercentOfCharge ?? 0,
  description: rule?.description || "",
  allowCancel: Boolean(rule?.allowCancel),
  requireReason: Boolean(rule?.requireReason),
  allowAutomaticRefund: Boolean(rule?.allowAutomaticRefund),
});

const numberInputProps = {
  min: 0,
  step: "any",
};

const CancellationPolicyFormModal = ({
  show,
  onClose,
  onSubmit,
  loading,
  initialRule,
  title,
}) => {
  const [form, setForm] = useState(DEFAULT_FORM);

  useEffect(() => {
    if (!show) return;
    setForm(initialRule ? fromRuleToForm(initialRule) : DEFAULT_FORM);
  }, [show, initialRule]);

  const submitLabel = useMemo(() => (initialRule ? "Update Rule" : "Create Rule"), [initialRule]);

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await onSubmit(toPayload(form));
  };

  return (
    <Modal show={show} onHide={onClose} centered size="lg" contentClassName="border-0 shadow-lg">
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Stage</Form.Label>
                <Form.Select value={form.stage} onChange={(e) => setField("stage", e.target.value)} required>
                  {STAGE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Canceler</Form.Label>
                <Form.Select value={form.canceler} onChange={(e) => setField("canceler", e.target.value)} required>
                  {CANCELLER_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>Effective From</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={form.effectiveFrom}
                  onChange={(e) => setField("effectiveFrom", e.target.value)}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Effective To</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={form.effectiveTo}
                  onChange={(e) => setField("effectiveTo", e.target.value)}
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label>Charge %</Form.Label>
                <Form.Control
                  type="number"
                  {...numberInputProps}
                  value={form.chargePercent}
                  onChange={(e) => setField("chargePercent", e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Charge Fixed Amount</Form.Label>
                <Form.Control
                  type="number"
                  {...numberInputProps}
                  value={form.chargeFixedAmount}
                  onChange={(e) => setField("chargeFixedAmount", e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Charge Base</Form.Label>
                <Form.Select value={form.chargeBase} onChange={(e) => setField("chargeBase", e.target.value)}>
                  {CHARGE_BASE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label>Helper Payout %</Form.Label>
                <Form.Control
                  type="number"
                  {...numberInputProps}
                  value={form.helperPayoutPercentOfCharge}
                  onChange={(e) =>
                    setField("helperPayoutPercentOfCharge", e.target.value)
                  }
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Helper Payout Fixed</Form.Label>
                <Form.Control
                  type="number"
                  {...numberInputProps}
                  value={form.helperPayoutFixedAmount}
                  onChange={(e) => setField("helperPayoutFixedAmount", e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Oliveeb Fee %</Form.Label>
                <Form.Control
                  type="number"
                  {...numberInputProps}
                  value={form.oliveebFeePercentOfCharge}
                  onChange={(e) => setField("oliveebFeePercentOfCharge", e.target.value)}
                />
              </Form.Group>
            </Col>

            <Col md={12}>
              <Form.Group>
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={form.description}
                  onChange={(e) => setField("description", e.target.value)}
                />
              </Form.Group>
            </Col>

            <Col md={12}>
              <div className="d-flex flex-wrap gap-4">
                <Form.Check
                  type="switch"
                  label="Active"
                  checked={form.active}
                  onChange={(e) => setField("active", e.target.checked)}
                />
                <Form.Check
                  type="switch"
                  label="Allow Cancel"
                  checked={form.allowCancel}
                  onChange={(e) => setField("allowCancel", e.target.checked)}
                />
                <Form.Check
                  type="switch"
                  label="Require Reason"
                  checked={form.requireReason}
                  onChange={(e) => setField("requireReason", e.target.checked)}
                />
                <Form.Check
                  type="switch"
                  label="Allow Automatic Refund"
                  checked={form.allowAutomaticRefund}
                  onChange={(e) => setField("allowAutomaticRefund", e.target.checked)}
                />
              </div>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={loading}>
            {loading ? <Spinner size="sm" animation="border" className="me-2" /> : null}
            {submitLabel}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CancellationPolicyFormModal;
